import type { ButtonHTMLAttributes, ComponentProps } from 'react';
import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  ColorProps,
  FontWeightProps,
  MarginProps,
  MinWidthProps,
  SpaceProps,
} from 'styled-system';
import {
  alignItems,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  color,
  fontWeight,
  minWidth,
  size,
  space,
  themeGet,
} from 'styled-system';

import type { ButtonBaseProps, ThemedButtonWithHTMLAttrs } from '../Button';
import { Button } from '../Button';
import Icon from '../Icon';
import { SubtitleST3 } from '../Typography';

interface LinkProps {
  href?: string;
}

type ButtonStyleProps = AlignItemsProps &
  BorderProps &
  BorderStyleProps &
  BorderColorProps &
  BorderWidthProps &
  BorderRadiusProps &
  ColorProps &
  FontWeightProps &
  MinWidthProps &
  SpaceProps &
  LinkProps;

export interface LoadButtonProps
  extends ButtonBaseProps, ButtonHTMLAttributes<unknown>, LinkProps, MarginProps {
  dataTestId?: string;
  isRounded?: boolean;
  ariaLabel?: string;
}

const ICON_SIZE = 28;
const ROUNDED_ICON_SIZE = 16;
const BUTTON_MIN_WIDTH = '138px';
const ROUNDED_BUTTON_MIN_WIDTH = '98px';

const StyledButton: ThemedButtonWithHTMLAttrs<ButtonStyleProps> = styled(Button)`
  ${alignItems};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border};
  ${borderRadius};
  ${color};
  ${fontWeight};
  ${minWidth};
  ${space};

  cursor: ${({ isLoading }) => (isLoading ? 'wait' : 'pointer')};
  transition: all 0.3s ease-in-out;
  overflow-anchor: none;
  text-decoration: none;

  &[aria-disabled='true'] {
    color: white;
    cursor: not-allowed;
    background-color: ${themeGet('colors.sys.neutral.border.hover')};
    border-color: white;
    opacity: 0.3;
  }
`;

const buttonStyles = {
  kind: 'secondary',
  alignItems: 'center',
  borderStyle: 'solid',
  borderWidth: 'small',
  borderColor: 'sys.neutral.border.hover',
  borderRadius: 'small',
  color: 'sys.neutral.border.active',
  fontWeight: 'bold',
  minWidth: BUTTON_MIN_WIDTH,
  spinnerSize: ICON_SIZE,
  spinnerMargin: '0 auto',
  py: 'sp_8',
  px: 'sp_0',
};

const RoundedStyledButton = styled(StyledButton)<any>``;

const roundedButtonStyles = {
  kind: 'secondary',
  alignItems: 'center',
  borderStyle: 'solid',
  borderWidth: 'small',
  borderColor: 'sys.neutral.border.default',
  borderRadius: 'x-large',
  color: 'sys.neutral.text.default',
  fontWeight: 'normal',
  spinnerMargin: '0 auto',
  minWidth: ROUNDED_BUTTON_MIN_WIDTH,
  p: 'sp_12',
};

const LoadMoreIcon = styled(Icon)<any>`
  ${size};
  ${space};
`;

const loadMoreIconStyles = {
  name: 'plusStrong',
  pl: 'sp_4',
  role: 'presentation',
  size: ICON_SIZE,
};

const RoundedLoadMoreIcon = styled(LoadMoreIcon)<any>``;

const roundedLoadMoreIconStyles = {
  ...loadMoreIconStyles,
  name: 'plusFilled',
  mr: 'sp_8',
  size: ROUNDED_ICON_SIZE,
  px: 'sp_0',
};

const buttonLabelStyles: ComponentProps<typeof SubtitleST3> = {
  pr: 'sp_12',
};

const LoadMoreButton = ({ isRounded }: { isRounded: boolean }) =>
  isRounded ? (
    <RoundedLoadMoreIcon {...roundedLoadMoreIconStyles} dataTestId="plus-filled" />
  ) : (
    <LoadMoreIcon {...loadMoreIconStyles} dataTestId="plus-strong" />
  );

const useCallbackHandler = (disabled: boolean, onClick: LoadButtonProps['onClick']) =>
  useCallback(
    event => {
      event.preventDefault();

      if (!disabled && onClick) {
        onClick(event);
      }
    },
    [onClick, disabled]
  );

const LoadButton = ({
  href,
  isLoading,
  disabled,
  onClick,
  children,
  isRounded,
  ariaLabel,
}: LoadButtonProps) => {
  const clickHandler = useCallbackHandler(disabled, onClick);
  const ButtonComponent = isRounded ? RoundedStyledButton : StyledButton;
  const hasHref = isLoading ? false : typeof href !== 'undefined';

  return (
    <ButtonComponent
      onClick={hasHref ? clickHandler : onClick}
      isLoading={isLoading}
      disabled={disabled}
      aria-label={ariaLabel || (children as string)}
      {...(isRounded ? roundedButtonStyles : buttonStyles)}
      {...(hasHref ? { href, as: 'a' } : {})}
    >
      <LoadMoreButton isRounded={isRounded} />
      <SubtitleST3 as="span" {...(!isRounded ? buttonLabelStyles : {})}>
        {children}
      </SubtitleST3>
    </ButtonComponent>
  );
};

export default LoadButton;
